import { Tooltip } from '@forethought-technologies/forethought-elements';
import { Box, useTheme } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import type { SendTrackingEventParameters } from 'api/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import { selectBannerImageConfig } from 'reducers/config';
import { selectFeatureFlags } from 'reducers/workflows';

const isUrlValid = (url: string | null) => {
  const allowedSchemes = ['http:', 'https:', 'mailto:'];

  try {
    const parsedUrl = new URL(url ?? '');
    return allowedSchemes.includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

export const BannerImage = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [showHideBannerIcon, setShowHideBannerIcon] = React.useState(false);
  const [showBanner, setShowBanner] = React.useState(true);
  const bannerImageConfig = useAppSelector(selectBannerImageConfig);
  const featureFlags = useAppSelector(selectFeatureFlags);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowHideBannerIcon(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const emitEvent = (event: SendTrackingEventParameters) =>
    dispatch(sendTrackingEvent(event));

  if (
    !featureFlags.includes('banner_image') ||
    !bannerImageConfig.is_enabled ||
    !bannerImageConfig.image ||
    !showBanner
  ) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', position: 'relative', width: '100%' }}>
      {showHideBannerIcon && (
        <Box
          sx={{
            backgroundColor: '#0e0e0e',
            borderRadius: '4px',
            color: theme.palette.colors.white,
            height: '20px',
            position: 'absolute',
            right: '5px',
            top: '5px',
            transition: 'all 0.3s ease-in-out',
            width: '20px',
          }}
        >
          <Tooltip tooltipContent='Hide Banner'>
            <Box
              component='button'
              onClick={() => {
                setShowBanner(false);
                emitEvent({ event_type: 'widget-banner-image-closed' });
              }}
              sx={{
                alignItems: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                height: '20px',
                justifyContent: 'center',
                padding: 0,
                paddingBlock: 0,
                paddingInline: 0,
                width: '20px',
              }}
            >
              <IconX color={theme.palette.colors.white} size={15} />
            </Box>
          </Tooltip>
        </Box>
      )}
      {bannerImageConfig.link ? (
        <Box
          component='a'
          href={
            isUrlValid(bannerImageConfig.link) ? bannerImageConfig.link : ''
          }
          onClick={() =>
            emitEvent({ event_type: 'widget-banner-image-clicked' })
          }
          rel='noopener noreferrer'
          sx={{ lineHeight: 0, width: '100%' }}
          target='_blank'
        >
          <Box
            alt={bannerImageConfig.alt_text || ''}
            component='img'
            src={bannerImageConfig.image}
            sx={{ width: '100%' }}
          />
        </Box>
      ) : (
        <Box
          alt={bannerImageConfig.alt_text || ''}
          component='img'
          src={bannerImageConfig.image}
          sx={{ width: '100%' }}
        />
      )}
    </Box>
  );
};
